import { lmaReduxStore } from '@redux/store';
import { IPreviewResponse, IPromptVersion, IPromptView } from '../interfaces/IPrompt';

// Same-origin path proxied by nginx to lma-scheduler (/api/v1/briefing-prompts).
const BASE = '/briefing-api/briefing-prompts';

function headers(json = false): Record<string, string> {
  const token = lmaReduxStore.getState().auth?.tokenId;
  const h: Record<string, string> = {};
  if (token) {
    h.token = token;
  }
  if (json) {
    h['Content-Type'] = 'application/json';
  }
  return h;
}

async function ensureOk(res: Response): Promise<Response> {
  if (!res.ok) {
    // The save endpoint rejects templates with unknown placeholders or without the JSON-output
    // instruction and explains why in an { error } body — surface that instead of a bare status,
    // otherwise the author has no idea what to fix.
    const detail = await res
      .clone()
      .json()
      .then((body) => (body && body.error ? String(body.error) : ''))
      .catch(() => '');
    throw new Error(detail || `Prompt-API ${res.status}`);
  }
  return res;
}

export async function listPrompts(): Promise<IPromptView[]> {
  const res = await ensureOk(await fetch(BASE, { headers: headers() }));
  return res.json();
}

export async function savePrompt(p: IPromptView): Promise<void> {
  await ensureOk(
    await fetch(BASE, {
      method: 'PUT',
      headers: headers(true),
      body: JSON.stringify({ signalType: p.signalType, scopeKey: p.scopeKey, name: p.name, template: p.template }),
    })
  );
}

export async function deletePrompt(signalType: string, scopeKey: string): Promise<void> {
  const url = `${BASE}?signalType=${encodeURIComponent(signalType)}&scopeKey=${encodeURIComponent(scopeKey)}`;
  await ensureOk(await fetch(url, { method: 'DELETE', headers: headers() }));
}

/**
 * Previews a template. When `orgKey` and `date` name a stored finding, the backend previews against
 * that finding's real facts; otherwise a signal-appropriate sample is used.
 */
export async function previewPrompt(
  signalType: string,
  template: string,
  orgKey?: string,
  date?: string
): Promise<IPreviewResponse> {
  const res = await ensureOk(
    await fetch(`${BASE}/preview`, {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({ signalType, template, orgKey, date }),
    })
  );
  return res.json();
}

/** The version history of a prompt, newest first. */
export async function getPromptHistory(signalType: string, scopeKey: string): Promise<IPromptVersion[]> {
  const url = `${BASE}/history?signalType=${encodeURIComponent(signalType)}&scopeKey=${encodeURIComponent(scopeKey)}`;
  const res = await ensureOk(await fetch(url, { headers: headers() }));
  return res.json();
}

/** Restores an earlier version of a prompt (recorded as a new version). */
export async function rollbackPrompt(signalType: string, scopeKey: string, versionAt: string): Promise<void> {
  await ensureOk(
    await fetch(`${BASE}/rollback`, {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({ signalType, scopeKey, versionAt }),
    })
  );
}

export async function suggestPrompt(signalType: string, currentTemplate: string): Promise<string> {
  const res = await ensureOk(
    await fetch(`${BASE}/suggest`, {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({ signalType, currentTemplate }),
    })
  );
  const json = await res.json();
  return json.suggestion || '';
}
